import { Stack, ToggleButton, Typography, useTheme } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

export type DayOfWeekValue = "sunday" | "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday";

interface DayOfWeekSelector {
  days?: Array<DayOfWeekValue>;
  onChange(days: Array<DayOfWeekValue>): void;
}

interface DayOption {
  abbr: string;
  label: string;
  value: DayOfWeekValue;
}

export default function DayOfWeekSelector(props: DayOfWeekSelector): React.JSX.Element {
  const { i18n, t } = useTranslation();
  const theme = useTheme();

  const [days, setDays] = useState<Array<DayOfWeekValue>>(props.days ?? []);

  function handleDayChange(event: React.MouseEvent<HTMLElement>, value: DayOfWeekValue) {
    if (days.includes(value)) {
      const newDays = days.filter((day) => {
        return day !== value;
      });
      setDays(newDays);
      props.onChange(newDays);
    } else {
      const newDays = [...days, value];
      setDays(newDays);
      props.onChange(newDays);
    }
  }

  const dayOptions: Array<DayOption> = [{
    abbr: t("days-of-week.sunday.abbr"),
    label: t("day-of-week-selector.buttons.sunday"),
    value: "sunday"
  }, {
    abbr: t("days-of-week.monday.abbr"),
    label: t("day-of-week-selector.buttons.monday"),
    value: "monday"
  }, {
    abbr: t("days-of-week.tuesday.abbr"),
    label: t("day-of-week-selector.buttons.tuesday"),
    value: "tuesday"
  }, {
    abbr: t("days-of-week.wednesday.abbr"),
    label: t("day-of-week-selector.buttons.wednesday"),
    value: "wednesday"
  }, {
    abbr: t("days-of-week.thursday.abbr"),
    label: t("day-of-week-selector.buttons.thursday"),
    value: "thursday"
  }, {
    abbr: t("days-of-week.friday.abbr"),
    label: t("day-of-week-selector.buttons.friday"),
    value: "friday"
  }, {
    abbr: t("days-of-week.saturday.abbr"),
    label: t("day-of-week-selector.buttons.saturday"),
    value: "saturday"
  }];

  const selectedDays = dayOptions.reduce((result: Array<string>, dayOption) => {
    if (days.includes(dayOption.value)) {
      result.push(dayOption.abbr);
    }
    return result;
  }, []);
  const selectedDaysString = selectedDays.join(", ");

  return (
    <>
      <Stack direction="row" spacing={1.5}>
        {dayOptions.map((dayOption) => {
          return (
            <ToggleButton
              key={dayOption.value}
              onChange={handleDayChange}
              selected={days.includes(dayOption.value)}
              sx={{ 
                borderRadius: 10,
                borderWidth: 2,
                height: "40px",
                width: "40px",
                "&:hover": {
                  backgroundColor: theme.palette.background.default,
                  borderColor: theme.colors.primary.main,
                  borderWidth: 2,
                  color: theme.colors.primary.main
                }
              }}
              value={dayOption.value} >
                {dayOption.label}
            </ToggleButton>
          )
        })}
      </Stack>
      <Typography>
        {selectedDaysString ? selectedDaysString : t("day-of-week-selector.selection-empty")}
      </Typography>
    </>
  )
}